import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  CircularProgress,
  Typography,
  Checkbox,
} from "@mui/material";
import { useToast } from "contexts/ToastContext";
import { useCallback, useEffect, useState } from "react";
import { useCelleraTheme } from "theme/useCelleraTheme";

interface CancelPatientRequestModalProps {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly onSubmit: () => Promise<void>;
  readonly patientEmail?: string;
}

function CancelPatientRequestModal({
  open,
  onClose,
  onSubmit,
  patientEmail,
}: CancelPatientRequestModalProps) {
  const { colors } = useCelleraTheme();
  const { showSuccess, showError } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isUnderstood, setIsUnderstood] = useState(false);

  useEffect(() => {
    if (!open) {
      setIsUnderstood(false);
    }
  }, [open]);

  const handleClose = useCallback(() => {
    if (!isSubmitting) {
      setIsUnderstood(false);
      onClose();
    }
  }, [isSubmitting, onClose]);

  const handleConfirm = useCallback(async () => {
    try {
      setIsSubmitting(true);
      await onSubmit();
      showSuccess(
        "Request Canceled",
        "Patient data access request has been canceled successfully."
      );
      handleClose();
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : "Failed to cancel request. Please try again.";
      showError("Cancellation Failed", message);
    } finally {
      setIsSubmitting(false);
    }
  }, [onSubmit, showSuccess, showError, handleClose]);

  return (
    <Dialog
      open={open}
      onClose={isSubmitting ? undefined : handleClose}
      fullWidth
      disableEscapeKeyDown={isSubmitting}
      slotProps={{
        paper: {
          sx: { borderRadius: 2, minWidth: "650px" },
        },
      }}
    >
      <DialogTitle>
        <Typography
          variant="h5"
          sx={{
            fontWeight: 600,
            color: colors.text.primary,
            fontSize: "24px",
            paddingBottom: "8px",
          }}
        >
          Cancel Patient Request
        </Typography>

        {/* Divider */}
        <Box
          sx={{
            width: "100%",
            height: "0.5px",
            backgroundColor: colors.background.grayDivider,
            flex: "none",
            alignSelf: "stretch",
            flexGrow: 0,
          }}
        />
      </DialogTitle>

      <DialogContent>
        <Box sx={{ pt: 1 }}>
          <Typography
            sx={{
              color: colors.text.primary,
              fontSize: "16px",
              lineHeight: 1.6,
              mb: 2,
            }}
          >
            Are you sure you want to cancel this request?
            <br />
            {`Once canceled, the patient will be notified and the request cannot be resumed.`}
          </Typography>

          {patientEmail && (
            <Typography
              sx={{
                color: colors.text.secondary,
                fontSize: "14px",
                mb: 2,
              }}
            >
              Patient email: <strong>{patientEmail}</strong>
            </Typography>
          )}
        </Box>

        <Box display="flex" alignItems="center" gap={1} paddingBottom="8px">
          <Checkbox
            checked={isUnderstood}
            onChange={(e) => setIsUnderstood(e.target.checked)}
            disabled={isSubmitting}
            sx={{
              color: colors.primary.main,
              "&.Mui-checked": {
                color: colors.primary.main,
              },
              paddingLeft: 0,
            }}
          />
          <Typography
            fontSize="14px"
            fontWeight={500}
            color={colors.text.primary}
          >
            I understand
          </Typography>
        </Box>

        {/* Divider */}
        <Box
          sx={{
            width: "100%",
            height: "0.5px",
            backgroundColor: colors.background.grayDivider,
            flex: "none",
            alignSelf: "stretch",
            flexGrow: 0,
          }}
        />
      </DialogContent>

      <DialogActions sx={{ padding: "16px", justifyContent: "flex-end" }}>
        <Button
          onClick={handleClose}
          disabled={isSubmitting}
          sx={{
            minWidth: "150px",
            textTransform: "none",
            padding: "8px 24px",
            borderRadius: "24px",
            fontSize: "16px",
            color: colors.text.secondary,
            fontWeight: 500,
            border: `1px solid ${colors.border.light}`,
            "&:hover": { backgroundColor: colors.background.highlight },
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          disabled={!isUnderstood || isSubmitting}
          sx={{
            minWidth: "150px",
            textTransform: "none",
            padding: "8px 24px",
            borderRadius: "24px",
            fontSize: "16px",
            fontWeight: 600,
            backgroundColor: colors.error[700],
            color: colors.primary.contrast,
            "&:hover": { backgroundColor: colors.error[700] },
            "&:disabled": {
              backgroundColor: colors.border.main,
              color: colors.text.disabled,
            },
          }}
        >
          {isSubmitting ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            "Confirm"
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default CancelPatientRequestModal;
